import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import {
  PasswordRequirement,
  PasswordRequirements,
} from '@/components/password-requirements';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface NewPasswordFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  disabled: boolean;
  name: Path<T>;
  label: string;
  description?: string;
  autoComplete?: string;
  passwordStrength: number;
}

export function NewPasswordField<T extends FieldValues>({
  form,
  disabled,
  name,
  label,
  autoComplete,
  passwordStrength,
}: NewPasswordFieldProps<T>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="font-semibold">{label}</FormLabel>
          <FormControl>
            <Input
              {...field}
              type="password"
              disabled={disabled}
              autoComplete={autoComplete}
            />
          </FormControl>
          <FormMessage className="font-semibold" />
          <Accordion type="single" collapsible>
            <AccordionItem value="password-requirements" className="border-0">
              <AccordionTrigger className="py-0">
                <Progress
                  value={passwordStrength}
                  className="h-2 mr-1"
                  progressClassname={cn({
                    'bg-destructive': passwordStrength < 50,
                    'bg-warning':
                      passwordStrength >= 50 && passwordStrength < 75,
                    'bg-success': passwordStrength >= 75,
                  })}
                />
              </AccordionTrigger>
              <AccordionContent>
                <PasswordRequirement
                  label="Includes at least 8 characters"
                  meets={form.getValues(name).length > 8}
                />
                <PasswordRequirements password={form.getValues(name)} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </FormItem>
      )}
    />
  );
}
